import React from "react";
import styled from "styled-components";
import Fade from "react-reveal/Fade";

const MAIL_SUBJECT = "Lead Intake & Follow-Up Installation: $900";
const MAIL_BODY = `Hi James,

I'd like to start the $900 Lead Intake & Follow-Up Installation.

Business name:
Owner name:
City / area:
Best email for Google access:
Best phone:

Thanks`;

const MAILTO_HREF = `mailto:james@studiovoice2fly.com?subject=${encodeURIComponent(
  MAIL_SUBJECT
)}&body=${encodeURIComponent(MAIL_BODY)}`;

const deliverables = [
  "One public inquiry form, plus one form for phone leads",
  "One Google Sheet with a simple status field",
  "Automatic confirmation to the lead",
  "Owner notice for every new lead",
  "One owner reminder for still-open leads",
  "At most one follow-up message to the lead",
  "Runs in accounts you own",
  "Tested path, written instructions, and a 30-minute handoff",
];

const LeadInstallSection = ({ embedded = false }) => {
  return (
    <Section
      id="lead-install"
      aria-labelledby="lead-install-heading"
      $embedded={embedded}
    >
      <Container $embedded={embedded}>
        <Content $embedded={embedded}>
          <Fade bottom cascade damping={0.2}>
            <TopLine>Lead Intake & Follow-Up Installation</TopLine>
            <Heading id="lead-install-heading" $embedded={embedded}>
              Inquiries get captured, acknowledged, and followed up once
            </Heading>
            <Subhead>
              I set up one straightforward lead intake and follow-up process in
              your own accounts, test it, and hand it over for a fixed $900. No
              continuing contract.
            </Subhead>

            <Deliverables>
              {deliverables.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </Deliverables>

            <PriceLine>
              $900 fixed · one-time install · 50% to schedule, 50% at acceptance
              · delivery within 5 business days after you provide access
            </PriceLine>

            <Qualifier>
              Fit: one owner, one location, non-sensitive inquiries. Not
              included: CRM integration, SMS, AI assistants, or ongoing
              monitoring.
            </Qualifier>

            <ButtonWrap data-cta-wrap="true">
              <Cta href={MAILTO_HREF}>Start the $900 install</Cta>
            </ButtonWrap>
          </Fade>
        </Content>
      </Container>
    </Section>
  );
};

export default LeadInstallSection;

const Section = styled.section`
  border-top: ${({ $embedded }) => ($embedded ? "none" : "1px solid #45fe47")};

  @media (max-width: 768px) {
    border-top: 1px solid #45fe47;
  }
`;

const Container = styled.div`
  padding: ${({ $embedded }) => ($embedded ? "56px 36px 56px" : "80px 24px 40px")};
  margin: 0 auto;
  max-width: ${({ $embedded }) => ($embedded ? "none" : "1200px")};
  display: grid;
  justify-content: ${({ $embedded }) => ($embedded ? "stretch" : "center")};
  background: ${({ $embedded }) =>
    $embedded
      ? "transparent"
      : `linear-gradient(
    135deg,
    rgba(15, 15, 15, 0.9),
    rgba(30, 30, 30, 0.8)
  )`};
  backdrop-filter: ${({ $embedded }) => ($embedded ? "none" : "blur(10px)")};
  border-radius: 0;
  border-bottom: none;
  border-left: ${({ $embedded }) => ($embedded ? "none" : "1px solid #45fe47")};
  border-right: ${({ $embedded }) => ($embedded ? "none" : "1px solid #45fe47")};
  color: #fff;
  height: ${({ $embedded }) => ($embedded ? "100%" : "auto")};
  box-sizing: border-box;

  @media (max-width: 1200px) {
    margin: ${({ $embedded }) => ($embedded ? "0" : "0 25px")};
  }

  @media (max-width: 768px) {
    padding: 60px 20px 0;
    margin: 0;
    justify-content: center;
    border-radius: 0;
    border-bottom: none;
    border-left: none;
    border-right: none;
    background: #45fe47;
    color: #010606;
    backdrop-filter: none;
    height: auto;
  }
`;

const Content = styled.div`
  max-width: ${({ $embedded }) => ($embedded ? "none" : "800px")};
  width: 100%;
  padding-bottom: ${({ $embedded }) => ($embedded ? "0" : "20px")};

  @media (max-width: 768px) {
    padding-bottom: 40px;
  }
`;

const TopLine = styled.p`
  font-family: "VT323", monospace;
  color: #45fe47;
  font-size: 16px;
  line-height: 16px;
  font-weight: 700;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  margin: 0 0 16px;

  @media (max-width: 768px) {
    color: #000;
  }
`;

const Heading = styled.h2`
  font-family: "VT323", monospace;
  color: #f7f8fa;
  font-size: ${({ $embedded }) => ($embedded ? "36px" : "48px")};
  line-height: 1.1;
  font-weight: 600;
  margin: 0 0 20px;

  @media (max-width: 768px) {
    color: #000;
    font-size: 30px;
    line-height: 1.15;
    margin-bottom: 16px;
  }

  @media (max-width: 480px) {
    font-size: 28px;
  }
`;

const Subhead = styled.p`
  font-family: "Encode Sans Expanded", sans-serif;
  color: #f7f8fa;
  font-size: 16px;
  line-height: 1.55;
  margin: 0 0 22px;

  @media (max-width: 768px) {
    color: #010606;
    font-size: 15px;
    line-height: 1.55;
    margin-bottom: 18px;
  }
`;

const Deliverables = styled.ul`
  font-family: "Encode Sans Expanded", sans-serif;
  color: #f7f8fa;
  font-size: 15px;
  line-height: 1.5;
  margin: 0 0 22px;
  padding-left: 1.15em;
  list-style: disc;

  li {
    font-family: "Encode Sans Expanded", sans-serif;
    margin-bottom: 8px;
  }

  li:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    color: #010606;
    font-size: 14px;
    line-height: 1.5;
    margin-bottom: 18px;
    padding-left: 1.1em;

    li {
      font-family: "Encode Sans Expanded", sans-serif !important;
      color: #010606;
      font-size: 14px;
      line-height: 1.5;
      margin-bottom: 8px;
    }
  }
`;

const PriceLine = styled.p`
  font-family: "Encode Sans Expanded", sans-serif;
  color: #45fe47;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.45;
  margin: 0 0 14px;

  @media (max-width: 768px) {
    color: #010606;
    font-size: 14px;
    line-height: 1.5;
    margin-bottom: 12px;
  }
`;

const Qualifier = styled.p`
  font-family: "Encode Sans Expanded", sans-serif;
  color: #888;
  font-size: 13px;
  line-height: 1.45;
  margin: 0 0 28px;

  @media (max-width: 768px) {
    color: #010606;
    font-size: 13px;
    line-height: 1.5;
    margin-bottom: 24px;
  }
`;

const ButtonWrap = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const Cta = styled.a`
  font-family: "VT323", monospace;
  background: #45fe47;
  color: #010606;
  border: 1px solid #010606;
  padding: 12px 32px;
  font-size: 18px;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    background: #010606;
    color: #f7f8fa;
    border-color: #000;
  }

  @media (max-width: 768px) {
    background: #010606;
    color: #45fe47;
    border-color: #010606;

    &:hover {
      background: #fff;
      color: #010606;
      border-color: #010606;
    }
  }
`;
